/**
 * Navigation actions shared by tree, palette, wikilinks, and shortcuts.
 * Items are resolved across every mounted space (personal + shared folders);
 * mutations respect shared-folder roles (viewer read-only, trash owner-only).
 */
import { allContexts, ctxFor, getData, resolveItem, type DataContext } from "@/data/store";
import { useSharedFolders } from "@/data/sharedFolders";
import { useTabs } from "@/store/tabs";
import { useRecents } from "@/store/recents";
import { useExpanded } from "@/store/expanded";
import { useUi } from "@/store/ui";
import { toast } from "@/ui/toast";
import type { SharedRole } from "@hydra-notes/shared";

/** The caller's role in a context ("owner" for the personal workspace). */
export function roleIn(ctx: DataContext): SharedRole {
  if (ctx.kind === "personal") return "owner";
  return useSharedFolders.getState().folders.find((f) => f.id === ctx.workspaceId)?.role ?? "viewer";
}

export function canEditIn(ctx: DataContext): boolean {
  const role = roleIn(ctx);
  return role === "owner" || role === "editor";
}

export function openItem(itemId: string, opts?: { newTab?: boolean }) {
  const res = resolveItem(itemId);
  if (!res || res.item.trashedAt) return;
  if (res.item.type === "tasklist") {
    useUi.getState().openTaskView(itemId);
    useRecents.getState().touch(itemId);
    return;
  }
  useUi.getState().setActiveApp("notes");
  useTabs.getState().openItem(itemId, opts);
  useRecents.getState().touch(itemId);
  revealInTree(itemId);
}

export function revealInTree(itemId: string) {
  const res = resolveItem(itemId);
  if (!res) return;
  const ws = res.ctx.workspace;
  const ancestors: string[] = [];
  let cur = res.item.parentId;
  let hops = 0;
  while (cur && hops++ < 100) {
    ancestors.push(cur);
    cur = ws.getItem(cur)?.parentId ?? null;
  }
  // the folder header row of a shared space expands by its space id
  if (res.ctx.kind === "shared") ancestors.push(`sf:${res.ctx.workspaceId}`);
  if (ancestors.length) useExpanded.getState().expandPath(ancestors);
}

/** Create a note. parentId may live in any mounted space (role permitting). */
export function createNote(parentId: string | null = null, title = ""): string | null {
  const ctx = parentId ? (resolveItem(parentId)?.ctx ?? getData()) : getData();
  if (!canEditIn(ctx)) {
    toast.error("You have view-only access to this folder.");
    return null;
  }
  const parent = parentId ? ctx.workspace.getItem(parentId) : undefined;
  const effectiveParent = parent && parent.type === "folder" ? parent.id : null;
  const id = ctx.workspace.createItem("note", effectiveParent, title);
  openItem(id, { newTab: true });
  return id;
}

export function createFolder(parentId: string | null = null, title = ""): string | null {
  const ctx = parentId ? (resolveItem(parentId)?.ctx ?? getData()) : getData();
  if (!canEditIn(ctx)) {
    toast.error("You have view-only access to this folder.");
    return null;
  }
  return ctx.workspace.createItem("folder", parentId, title);
}

/**
 * Open a note by title or alias (wikilink navigation). Resolution order:
 * the SOURCE note's space first, then personal, then other mounted spaces.
 * A missing note is auto-created in the personal workspace only, never
 * inside a shared folder (fromSpaceId editors aside, a stray wikilink click
 * should not add notes to a team folder).
 *
 * Pass `create: false` to offer creation instead of doing it. The graph view
 * uses that: a stray click on one of hundreds of unresolved nodes silently
 * spawning notes is a worse failure than one extra click.
 */
export function openByTitle(title: string, fromSpaceId?: string, opts?: { create?: boolean }) {
  const t = title.trim().toLowerCase();
  const matchIn = (ctx: DataContext) =>
    ctx.workspace
      .getSnapshot()
      .find(
        (i) =>
          !i.trashedAt &&
          (i.title.trim().toLowerCase() === t || (i.aliases ?? []).some((a) => a.trim().toLowerCase() === t)),
      );

  const personal = getData();
  const source = fromSpaceId ? (ctxFor(fromSpaceId) ?? personal) : personal;
  const order: DataContext[] = [source];
  if (source !== personal) order.push(personal);
  for (const ctx of allSharedContexts()) {
    if (!order.includes(ctx)) order.push(ctx);
  }
  for (const ctx of order) {
    const match = matchIn(ctx);
    if (match) {
      openItem(match.id);
      return;
    }
  }
  if (opts?.create === false) {
    toast.info(`No note named "${title.trim()}" yet`, {
      label: "Create",
      onClick: () => openByTitle(title, fromSpaceId),
    });
    return;
  }
  const id = personal.workspace.createItem("note", null, title.trim());
  openItem(id, { newTab: true });
}

function allSharedContexts(): DataContext[] {
  return allContexts().filter((c) => c.kind === "shared");
}

export function trashItem(itemId: string) {
  const res = resolveItem(itemId);
  if (!res) return;
  if (res.item.builtin === "tasks") {
    toast.error("The default task list can't be deleted. Rename or recolor it instead.");
    return;
  }
  if (res.ctx.kind === "shared" && roleIn(res.ctx) !== "owner") {
    toast.error("Only the folder owner can trash notes in a shared folder.");
    return;
  }
  const ids = [itemId, ...collectDescendants(itemId)];
  res.ctx.workspace.trash(itemId);
  useTabs.getState().closeItemEverywhere(ids);
  useRecents.getState().remove(ids);
  const title = res.item.title || "Untitled";
  toast.success(`Moved "${title}" to Trash`, {
    label: "Undo",
    onClick: () => {
      if (!res.ctx.destroyed && res.ctx.workspace.getItem(itemId)?.trashedAt) res.ctx.workspace.restore(itemId);
    },
  });
}

/** Duplicate a note: fresh doc seeded from the source markdown, same space. */
export async function duplicateNote(noteId: string): Promise<void> {
  const res = resolveItem(noteId);
  if (!res || res.item.type !== "note") return;
  if (!canEditIn(res.ctx)) {
    toast.error("You have view-only access to this folder.");
    return;
  }
  const md = await res.ctx.notes.readMarkdown(noteId);
  const id = res.ctx.workspace.createItem("note", res.item.parentId, `${res.item.title || "Untitled"} (copy)`);
  const handle = res.ctx.notes.open(id);
  await handle.whenLoaded;
  if (md) {
    handle.doc.transact(() => {
      handle.text.insert(0, md);
    });
  }
  handle.release();
  openItem(id, { newTab: true });
  toast.success(`Duplicated "${res.item.title || "Untitled"}"`);
}

export function archiveItem(itemId: string) {
  const res = resolveItem(itemId);
  if (!res) return;
  if (res.item.builtin === "tasks") {
    toast.error("The default task list can't be archived.");
    return;
  }
  if (res.ctx.kind === "shared") {
    toast.error("Archive is for personal notes; shared folders don't have one.");
    return;
  }
  const ids = [itemId, ...collectDescendants(itemId)];
  res.ctx.workspace.archive(itemId);
  useTabs.getState().closeItemEverywhere(ids);
  useRecents.getState().remove(ids);
  const title = res.item.title || "Untitled";
  toast.success(`Archived "${title}"`, {
    label: "Undo",
    onClick: () => {
      const current = res.ctx.workspace.getItem(itemId);
      if (!res.ctx.destroyed && current?.archivedAt && !current.trashedAt) res.ctx.workspace.unarchive(itemId);
    },
  });
}

function collectDescendants(id: string): string[] {
  const ws = resolveItem(id)?.ctx.workspace;
  if (!ws) return [];
  const out: string[] = [];
  const walk = (pid: string) => {
    for (const child of ws.getSnapshot().filter((i) => i.parentId === pid)) {
      out.push(child.id);
      walk(child.id);
    }
  };
  walk(id);
  return out;
}
